import { useEffect, useState } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { chosenSites } from "app/common/services/chosenSites";
import { EmptyWebsiteQueryBar } from "app/components/compare/WebsiteQueryBar/EmptyWebsiteQueryBar";
import { EducationContainer } from "app/components/educationbar/educationContainer";
import { EDUCATION_BAR_PREFERENCES_KEY } from "app/components/educationbar/educationHook";
import { useEducationBarProminent } from "app/components/educationbar/useEducationBarProminent";
import { useEducationBarProminentContext } from "app/components/educationbar/useEducationBarProminentContext";
import PageTitle from "app/components/React/PageTitle/PageTitle";
import { ProjectCreation } from "app/modules/project-from-comparison/ProjectCreation";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PreferencesService } from "app/services/preferences/preferencesService";

import WebsiteAnalysisFilters from "../../pages/website-analysis/WebsiteAnalysisFilters";
import useLobSites from "../compare/WebsiteQueryBar/useLobSites";
import WebsiteQueryBar from "../compare/WebsiteQueryBar/WebsiteQueryBar";

import { SubNav } from "./SubNav";

interface IWebsiteAnalysisSubNavProps {
    filterRecentsBy?: StateOrName | StateOrName[];
    hideFavoriteButton?: boolean;
    suggestions?: ISite[];
    disableSubDomainsFilter?: boolean;
    removeSubDomainsFilter?: boolean;
    queryBarSearchHeader?: React.ReactNode;
    renderQueryBar?: () => React.ReactNode;
}

export const WebsiteAnalysisSubNav = ({
    filterRecentsBy,
    hideFavoriteButton = false,
    suggestions = null,
    disableSubDomainsFilter = false,
    removeSubDomainsFilter = false,
    queryBarSearchHeader,
    renderQueryBar,
}: IWebsiteAnalysisSubNavProps) => {
    const { state, params } = useCurrentStateAndParams();
    const { isPersistent } = useSelector((state) => state.QueryBarReducer);
    const [numberOfComparedItems, setNumberOfComparedItems] = useState<number>(0);
    const { isDismissed, isMinimized, handleMaximize, handleMinimize, isProminentEducationBannerShown } =
        useEducationBarProminentContext();

    const [hideEducationComponent, setHideEducationComponent] = useState(null);
    const { isPageTop } = useEducationBarProminent();
    const lobData = useLobSites();

    useEffect(() => {
        setNumberOfComparedItems(chosenSites.get().length);
        setHideEducationComponent(PreferencesService.get(EDUCATION_BAR_PREFERENCES_KEY) || false);
    }, [state, params]);

    const pageStateObj = proNavigator.current();

    if (pageStateObj.getSubNav) {
        return pageStateObj.getSubNav(numberOfComparedItems);
    }
    const educationData = window.education?.[pageStateObj?.stateNameForEductionBar || pageStateObj.name];

    const onCloseEducationComponent = () => {
        PreferencesService.add({ [EDUCATION_BAR_PREFERENCES_KEY]: true });
        setHideEducationComponent(true);
        handleMinimize();
    };

    const onEducationIconClick = () => {
        if (hideEducationComponent) {
            PreferencesService.add({ [EDUCATION_BAR_PREFERENCES_KEY]: false });
            setHideEducationComponent(false);
        }
        if (isMinimized) {
            handleMaximize();
        }
    };

    const isEducationComponentShown = isProminentEducationBannerShown
        ? !isMinimized && (isDismissed || !isPageTop)
        : !hideEducationComponent;

    return isPersistent ? (
        <SubNav
            topLeftComponent={
                renderQueryBar ? (
                    renderQueryBar()
                ) : (
                    <EmptyWebsiteQueryBar
                        lobData={lobData}
                        filterRecentsBy={filterRecentsBy}
                        suggestions={suggestions}
                        hideSidebarOverlay={false}
                        recentsDedupeFunction={state.viewConfig?.recentsDedupeFunction}
                        queryBarSearchHeader={queryBarSearchHeader}
                    />
                )
            }
            bottomLeftComponent={<PageTitle />}
        />
    ) : params.key?.length || params.industry ? (
        <SubNav
            topLeftComponent={
                renderQueryBar ? (
                    renderQueryBar()
                ) : (
                    <>
                        <WebsiteQueryBar
                            lobData={lobData}
                            filterRecentsBy={filterRecentsBy}
                            showCreateCustomIndustryCta={state.showCreateCustomIndustryCta}
                            renderFavoriteButton={hideFavoriteButton ? () => null : undefined}
                            recentsDedupeFunction={state.viewConfig?.recentsDedupeFunction}
                            queryBarSearchHeader={queryBarSearchHeader}
                        />
                        <ProjectCreation />
                    </>
                )
            }
            numberOfComparedItems={numberOfComparedItems}
            bottomRightComponent={
                <WebsiteAnalysisFilters
                    disableSubDomainsFilter={disableSubDomainsFilter}
                    removeSubDomainsFilter={removeSubDomainsFilter}
                />
            }
            bottomLeftComponent={
                <PageTitle
                    showEducationIcon={!!educationData ? !isEducationComponentShown : null}
                    onEducationIconClick={onEducationIconClick}
                />
            }
            education={
                isEducationComponentShown ? (
                    <EducationContainer
                        onCloseEducationComponent={onCloseEducationComponent}
                        isBig={false}
                    />
                ) : null
            }
        />
    ) : null;
};
